import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./ActionRow.css";
import { IReducer } from "../../state/reducer";
import { BrickInfoTotal } from "../../state/BrickInfoTotal/BrickInfoTotal";
import { SetInfoTotal } from "../../state/SetInfo/SetInfoTotal";
import { AlertTriangle, Check, X } from "react-feather";
import { meetsCostRequirements } from "../../state/BrickActions/Actions/_CostsAndOutcomes/meetsCostRequirements";
import { meetsCostRequirementsSets } from "../../state/BrickActions/Actions/_CostsAndOutcomes/meetsCostRequirementsSets";
import { ActionChange } from "../../state/BrickActions/ActionInfo/ActionInfo";
import { meetsCostRequirementsSingle } from "../../state/BrickActions/Actions/_CostsAndOutcomes/meetsCostRequirementsSingle";
import { meetsCostRequirementsSingleSets } from "../../state/BrickActions/Actions/_CostsAndOutcomes/meetsCostRequirementsSingleSets";
import { isValidActionType } from "../../state/action";
import { lacksCapacity } from "../../state/BrickActions/Actions/_CostsAndOutcomes/lacksCapacity";
import { upgradeAtMaxCapacity } from "../../state/BrickActions/Actions/_CostsAndOutcomes/Upgrades/upgradeAtMaxCapacity";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ActionRowProps extends IReducer {
  id: string;
  title: string;
  type:
    | "Collect"
    | "Create"
    | "Split"
    | "Capacity"
    | "Auto Collect"
    | "BrickPack";
  details: string | null;
}

const ActionRow: React.FC<ActionRowProps> = ({
  id,
  title,
  type,
  details,
  state,
  dispatch,
}) => {
  // Ignore default action
  if (id === "default_action") return null;

  const [showPopup, setShowPopup] = useState(false);
  const [showCapacityTooltip, setShowCapacityTooltip] = useState(false);
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });

  const buttonRef = useRef<HTMLDivElement>(null);
  const capacityIconRef = useRef<SVGSVGElement>(null);

  // Drag and drop functionality
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !state.editModeActive });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: state.editModeActive ? "grab" : "pointer",
  };

  useEffect(() => {
    if (showPopup && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [showPopup]);

  useEffect(() => {
    if (showCapacityTooltip && capacityIconRef.current) {
      const rect = capacityIconRef.current.getBoundingClientRect();

      // Position tooltip above the icon with a small offset
      setTooltipPosition({
        top: rect.top - 35, // 35px above the icon
        left: rect.left + rect.width / 2, // Center horizontally on the icon
      });
    }
  }, [showCapacityTooltip]);

  const typeClass = `actionType${type.charAt(0).toUpperCase() + type.slice(1)}`;

  const renderRequirementsMet = () => {
    if (!state.actions.get(id)!.cost.length)
      return <Check size={25} className={"rowHeaderIcon metCostIcon hidden"} />;

    const action = state.actions.get(id)!;

    // Check if action has any set costs
    const hasSetCosts = action.cost.some((cost) => cost.type === "set");

    // Check if this is an Upgrade action at max capacity (100% find percentage)
    if (
      action.type === "Upgrade" &&
      upgradeAtMaxCapacity(action, state.actions, state.brickPackUpgrades)
    ) {
      return (
        <AlertTriangle
          ref={capacityIconRef}
          size={25}
          className={"rowHeaderIcon lacksCapacityIcon "}
          onMouseEnter={() => setShowCapacityTooltip(true)}
          onMouseLeave={() => setShowCapacityTooltip(false)}
        />
      );
    }

    if (lacksCapacity(action, state.bricks)) {
      return (
        <AlertTriangle
          ref={capacityIconRef}
          size={25}
          className={"rowHeaderIcon lacksCapacityIcon "}
          onMouseEnter={() => setShowCapacityTooltip(true)}
          onMouseLeave={() => setShowCapacityTooltip(false)}
        />
      );
    } else {
      // Use set-aware cost checking if action has set costs
      const costsMet = hasSetCosts
        ? meetsCostRequirementsSets(action, state.bricks, state.sets)
        : meetsCostRequirements(action, state.bricks);

      return costsMet ? (
        <Check size={25} className={"rowHeaderIcon metCostIcon"} />
      ) : (
        <X size={25} className={"rowHeaderIcon missingCostIcon"} />
      );
    }
  };

  const renderCosts = (index: number, cost: ActionChange) => {
    const action = state.actions.get(id)!;
    const trueCost =
      cost.baseChangeValue + action.costIncrement * action.costScale;

    // Default to "brick" if type not specified (backward compatibility)
    const costType = cost.type || "brick";

    if (costType === "brick") {
      // Render brick cost
      const currentAmount = state.bricks.get(cost.brickId)?.amount ?? 0;
      const displayAmount = isNaN(currentAmount) ? 0 : currentAmount;

      if (
        meetsCostRequirementsSingle(
          cost,
          action.costIncrement,
          action.costScale,
          state.bricks,
        )
      ) {
        return (
          <div className="popupCosts" key={index}>
            <Check size={20} className="costIcon metCostIcon" />
            {BrickInfoTotal.get(cost.brickId)?.name} ({displayAmount}/{trueCost}
            )
          </div>
        );
      } else {
        return (
          <div className="popupCosts" key={index}>
            <X size={20} className="costIcon missingCostIcon" />
            {BrickInfoTotal.get(cost.brickId)?.name} ({displayAmount}/{trueCost}
            )
          </div>
        );
      }
    } else if (costType === "set") {
      // Render set cost
      const currentCount = state.sets.get(cost.brickId)?.count ?? 0;

      if (
        meetsCostRequirementsSingleSets(
          cost,
          action.costIncrement,
          action.costScale,
          state.bricks,
          state.sets,
        )
      ) {
        return (
          <div className="popupCosts" key={index}>
            <Check size={20} className="costIcon metCostIcon" />
            {SetInfoTotal.get(cost.brickId)?.name} ({currentCount}/{trueCost})
          </div>
        );
      } else {
        return (
          <div className="popupCosts" key={index}>
            <X size={20} className="costIcon missingCostIcon" />
            {SetInfoTotal.get(cost.brickId)?.name} ({currentCount}/{trueCost})
          </div>
        );
      }
    }

    return null;
  };

  return (
    <>
      <div
        ref={(node) => {
          buttonRef.current = node;
          setNodeRef(node);
        }}
        style={style}
        {...attributes}
        {...listeners}
        className={`noSelect actionRowContainer ${typeClass} ${state.editModeActive ? "editMode" : ""} ${isDragging ? "dragging" : ""}`}
        onClick={
          state.editModeActive
            ? undefined
            : () => {
                const action = state.actions.get(id);
                // Prevent dispatch if Upgrade action is at max capacity
                if (
                  action &&
                  action.type === "Upgrade" &&
                  upgradeAtMaxCapacity(
                    action,
                    state.actions,
                    state.brickPackUpgrades,
                  )
                ) {
                  return; // Do nothing, just like when cost requirements aren't met
                }
                dispatch({ type: isValidActionType(id) ? id : "invalid" });
              }
        }
        onMouseEnter={
          state.editModeActive ? undefined : () => setShowPopup(true)
        }
        onMouseLeave={
          state.editModeActive ? undefined : () => setShowPopup(false)
        }
      >
        <div className="actionButtonDetails">
          <div className="actionRowHeader">
            <span className="rowType">
              {type === "Capacity" ? "Increase Capacity" : type}
            </span>
            <div className="actionRowTitle">{title}</div>
            <div className="rowHeaderParent">{renderRequirementsMet()}</div>
          </div>
        </div>
      </div>
      {showPopup &&
        createPortal(
          <div
            className="actionPopup"
            style={{ top: popupPosition.top, left: popupPosition.left }}
          >
            {state.actions.get(id)?.cost.length ? (
              <>
                <b className="costsHeader">Costs: </b>
                <div className="costContent">
                  {state.actions
                    .get(id)
                    ?.cost.map((cost, index) => renderCosts(index, cost))}
                </div>
              </>
            ) : null}
            {state.actions.get(id)!.costIncrement >= 1 ? (
              <div className="popupDetails">
                <b className="costIncrementHeader">Cost Increment: </b>
                <div className="costIncrementContent">
                  {state.actions.get(id)!.costIncrement}
                </div>
              </div>
            ) : null}
            {details ? (
              <div className="popupDetails">
                <b className="detailsHeader">Outcome: </b>
                <div className="detailsContent">{details}</div>
              </div>
            ) : null}
          </div>,
          document.body,
        )}
      {showCapacityTooltip &&
        createPortal(
          <div
            className="capacityTooltip"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
          >
            No available capacity
          </div>,
          document.body,
        )}
    </>
  );
};

export default ActionRow;
